import type { AuthAccountInfo } from '@pique/core/modules/cloud/entities/session';
import { z } from 'zod';

import type { CalendarAttendee } from './calendar-types';
import type { SerializedAttendee } from './schema';

// Strong typing for attendee creation - email is always required
export const CreateAttendeeSchema = z.object({
  email: z.string().email().min(1),
  name: z.string().optional(),
  avatar: z.string().optional(),
  present: z.boolean().default(true),
  self: z.boolean().default(false),
});

export type CreateAttendeeInput = z.infer<typeof CreateAttendeeSchema>;

/**
 * Simple hash function for browser compatibility
 */
function simpleHash(str: string): string {
  let hash = 0;
  if (str.length === 0) return hash.toString();
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Generate a stable, deterministic ID from an email address
 * This ensures the same person always gets the same ID across sessions
 */
export function generateStableAttendeeId(email: string): string {
  if (!email || !email.trim()) {
    throw new Error('Email is required to generate stable attendee ID');
  }

  // Normalize email for consistent hashing
  const normalizedEmail = email.toLowerCase().trim();

  // Create a stable hash-based ID
  const hash = simpleHash(normalizedEmail);

  // Use hash + prefix for readability
  return `attendee_${hash}`;
}

/**
 * Normalize email for consistent comparison
 */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/**
 * Convert CalendarAttendee to SerializedAttendee with stable ID
 */
export function calendarAttendeeToSerialized(
  calendarAttendee: CalendarAttendee
): SerializedAttendee {
  return {
    id: generateStableAttendeeId(calendarAttendee.email),
    name:
      calendarAttendee.displayName ||
      extractNameFromEmail(calendarAttendee.email),
    email: calendarAttendee.email,
    present: true, // Default for calendar attendees
    organizer: calendarAttendee.organizer ?? false,
    self: calendarAttendee.self ?? false,
    avatar: calendarAttendee.avatar ?? undefined,
    responseStatus: calendarAttendee.responseStatus ?? undefined,
  };
}

/**
 * Extract a display name from email if no name is provided
 */
function extractNameFromEmail(email: string): string {
  const localPart = email.split('@')[0] ?? '';
  return localPart
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

/**
 * Create attendee from auth service using centralized creation
 */
export function createAttendeeFromAuthUser(user: {
  email?: string | null;
  label?: string | null;
  avatar?: string | null;
}): SerializedAttendee {
  if (!user.email || !user.email.trim()) {
    throw new Error('Email is required to create attendee from auth user');
  }

  return createAttendee({
    email: user.email,
    name: user.label || undefined,
    avatar: user.avatar || undefined,
    present: true,
    self: true,
  });
}

/**
 * Create attendee from calendar data using centralized creation
 */
export function createAttendeeFromCalendar(
  calendarAttendee: CalendarAttendee
): SerializedAttendee {
  return createAttendee({
    email: calendarAttendee.email,
    name: calendarAttendee.displayName || undefined,
    avatar: calendarAttendee.avatar || undefined,
    present: true,
    self: calendarAttendee.self || false,
  });
}

/**
 * Centralized attendee creation with strong typing and email validation
 * This is the ONLY function that should be used to create attendees
 */
export function createAttendee(input: CreateAttendeeInput): SerializedAttendee {
  // Validate input with strong typing
  const validated = CreateAttendeeSchema.parse(input);
  const normalizedEmail = normalizeEmail(validated.email);

  return {
    id: generateStableAttendeeId(normalizedEmail),
    name: validated.name || extractNameFromEmail(normalizedEmail),
    email: normalizedEmail,
    present: validated.present,
    self: validated.self,
    avatar: validated.avatar,
  };
}

/**
 * Simple email-based deduplication
 * Since all attendees MUST have emails, this is much simpler
 */
export function deduplicateAttendeesByEmail(
  attendees: SerializedAttendee[]
): SerializedAttendee[] {
  const seenEmails = new Set<string>();
  return attendees.filter(attendee => {
    const normalizedEmail = normalizeEmail(attendee.email);
    if (seenEmails.has(normalizedEmail)) {
      return false;
    }
    seenEmails.add(normalizedEmail);
    return true;
  });
}

/**
 * Find attendee by email (normalized comparison)
 */
export function findAttendeeByEmail(
  attendees: SerializedAttendee[],
  email: string
): SerializedAttendee | undefined {
  const normalizedEmail = normalizeEmail(email);
  return attendees.find(a => normalizeEmail(a.email) === normalizedEmail);
}

/**
 * Check if attendee already exists by email
 */
export function attendeeExistsByEmail(
  attendees: SerializedAttendee[],
  email: string
): boolean {
  return findAttendeeByEmail(attendees, email) !== undefined;
}

/**
 * Create or update attendee using centralized creation
 * Simply delegates to createAttendee with proper typing
 */
export function createOrUpdateAttendee(
  existingAttendees: SerializedAttendee[],
  attendeeData: {
    email: string;
    name?: string;
    avatar?: string;
    present?: boolean;
    self?: boolean;
  }
): SerializedAttendee {
  const existing = findAttendeeByEmail(existingAttendees, attendeeData.email);

  if (existing) {
    // Update existing attendee but preserve stable ID
    return {
      ...existing,
      ...attendeeData,
      id: existing.id, // Keep the stable ID
      email: normalizeEmail(attendeeData.email), // Ensure normalized email
    };
  }

  // Create new attendee using centralized function
  return createAttendee(attendeeData as CreateAttendeeInput);
}

/**
 * Extract best first name from attendee for display
 */
export function extractBestFirstName(attendee: SerializedAttendee): string {
  if (attendee.name && attendee.name.trim() && !attendee.name.includes('@')) {
    const nameParts = attendee.name.trim().split(/\s+/);
    const firstName = nameParts[0] ?? '';
    if (firstName.length > 1 && !/[@.]/.test(firstName)) {
      return (
        firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
      );
    }
  }

  if (attendee.email) {
    const emailPart = attendee.email.split('@')[0] ?? '';
    const cleanedPart = (emailPart.replace(/[_.-]/g, ' ').split(/\s+/)[0] ?? '')
      .replace(/\d+$/g, '')
      .trim();

    if (cleanedPart.length > 1) {
      return (
        cleanedPart.charAt(0).toUpperCase() + cleanedPart.slice(1).toLowerCase()
      );
    }
  }

  return 'there';
}

/**
 * Generate prompt for addressing a single attendee in an email
 */
export function generateAttendeeNamePrompt(
  attendee: SerializedAttendee | null
): string {
  if (!attendee) return '';

  const firstName = extractBestFirstName(attendee);
  const fullName = attendee.name || attendee.email;

  return `Address the email to ${fullName}. Use "Hi ${firstName}" as the greeting. Ignore any other names from the transcript.`;
}

export interface AttendeeContextOptions {
  currentUser?: AuthAccountInfo | null;
  attendees?: SerializedAttendee[] | null;
  includeTranscriptionNote?: boolean;
}

/**
 * Generate context about attendees for AI prompts - USE THIS FOR ALL PROMPTS
 */
export function generateAttendeeContext(
  options: AttendeeContextOptions = {}
): string {
  const { currentUser, attendees, includeTranscriptionNote = true } = options;

  const contextParts: string[] = [];

  if (currentUser?.label && currentUser?.email) {
    contextParts.push(
      `The current user is ${currentUser.label} (${currentUser.email}).`
    );
  }

  if (attendees && attendees.length > 0) {
    const attendeeList = attendees
      .filter(attendee => attendee.email)
      .map(attendee => {
        const bestName = extractBestFirstName(attendee);
        if (
          attendee.name &&
          attendee.name.trim() &&
          !attendee.name.includes('@')
        ) {
          return `${attendee.name} (${attendee.email})`;
        }
        if (bestName && bestName !== 'there') {
          return `${bestName} (${attendee.email})`;
        }
        return attendee.email;
      })
      .join(', ');

    if (attendeeList) {
      contextParts.push(`The meeting attendees are: ${attendeeList}.`);
    }
  }

  if (
    includeTranscriptionNote &&
    (currentUser || (attendees && attendees.length > 0))
  ) {
    contextParts.push(
      `IMPORTANT: When referencing people mentioned in transcriptions or summaries, always use the correct NAMES ONLY provided above. Transcription may contain misspellings or variations of names - always correct these to match the exact attendee names listed. SPEAKER LABELING CONVENTION: Chunks labeled with an email address (e.g. "${currentUser?.email || 'user@example.com'}") are words spoken BY the current user (captured from their microphone). Chunks labeled "Speaker" are words spoken by OTHER meeting attendees (captured from system audio output), NOT by the current user. When "Speaker" appears, infer which non-user attendee is talking based on context, only when clearly determinable.`
    );
  }

  return contextParts.join(' ');
}

export function prependAttendeeContextToPrompt(
  basePrompt: string,
  options: AttendeeContextOptions = {}
): string {
  const context = generateAttendeeContext(options);
  if (!context) return basePrompt;

  const trimmedBase = basePrompt.trim();
  return `${context}\n\n${trimmedBase}`;
}

const AVATAR_COLORS = [
  '#4285F4',
  '#EA4335',
  '#FBBC05',
  '#34A853',
  '#E74C3C',
  '#F39C12',
  '#1ABC9C',
  '#8E44AD',
];

export function getColorFromName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? '#4285F4';
}

export function getInitials(name: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  const firstPart = parts[0] ?? '';
  if (parts.length === 1) {
    return firstPart.charAt(0).toUpperCase();
  }
  const lastPart = parts[parts.length - 1] ?? '';
  return (firstPart.charAt(0) + lastPart.charAt(0)).toUpperCase();
}

function createSvgAvatar(initials: string, bgColor: string): string {
  const fontSize = initials.length === 1 ? 24 : 18;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><circle cx="24" cy="24" r="24" fill="${bgColor}"/><text x="24" y="24" dy=".35em" fill="#fff" font-family="Outfit,-apple-system,BlinkMacSystemFont,sans-serif" font-size="${fontSize}" font-weight="600" text-anchor="middle">${initials}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const avatarCache = new Map<string, string>();

export function generateAvatarUrl(
  name: string,
  existingAvatarUrl?: string | null
): string | null {
  if (existingAvatarUrl) {
    return existingAvatarUrl;
  }

  if (!name || name.includes('@')) return null;

  const cached = avatarCache.get(name);
  if (cached) {
    return cached;
  }

  const initials = getInitials(name);
  const color = getColorFromName(name);
  const url = createSvgAvatar(initials, color);

  avatarCache.set(name, url);
  return url;
}
